import { PayloadAction } from '@reduxjs/toolkit';
import {
  Fit,
  Layout,
  useRive,
  useViewModel,
  useViewModelInstance,
  useViewModelInstanceBoolean,
  useViewModelInstanceNumber,
  useViewModelInstanceString,
  useViewModelInstanceTrigger,
} from '@rive-app/react-webgl2';
import { useEffect } from 'react';

import { useAppSelector } from '@/app/store/hooks';
import {
  balloonActions,
  BalloonStatus,
  selectBalloonMultiplier,
  selectBalloonStatus,
} from '@/entities/balloon';
import { useReduxAction } from '@/shared/lib/hooks/useReduxAction';

const ZERO = 0;

export function BalloonVisual() {
  const status = useAppSelector(selectBalloonStatus);
  const multiplier = useAppSelector(selectBalloonMultiplier);

  const { rive, RiveComponent } = useRive({
    src: '/assets/balloon/balloon.riv',
    stateMachines: 'State Machine 1',
    autoplay: true,
    layout: new Layout({ fit: Fit.Cover }),
  });

  const viewModel = useViewModel(rive);
  const vmInstance = useViewModelInstance(viewModel, { rive });

  const { trigger: triggerIdle } = useViewModelInstanceTrigger('trigger_idle', vmInstance);
  const { trigger: triggerHoldingOn } = useViewModelInstanceTrigger(
    'trigger_holding_on',
    vmInstance,
  );
  const { trigger: triggerHoldingOff } = useViewModelInstanceTrigger(
    'trigger_holding_off',
    vmInstance,
  );
  const { trigger: triggerCashout } = useViewModelInstanceTrigger('trigger_cashout', vmInstance);
  const { trigger: triggerBurst } = useViewModelInstanceTrigger('trigger_burst', vmInstance);
  const { trigger: triggerFinish } = useViewModelInstanceTrigger('trigger_finish', vmInstance);

  const { setValue: setMultiplier } = useViewModelInstanceString('multiplier', vmInstance);
  const { setValue: setFlightSpeed } = useViewModelInstanceNumber('flight_speed', vmInstance);
  const { setValue: setIsLuckyRive } = useViewModelInstanceBoolean('is_lucky', vmInstance);

  // Drive continuous state transitions
  useEffect(() => {
    if (!vmInstance) return;
    switch (status) {
      case BalloonStatus.IDLE:
        triggerIdle();
        setMultiplier(`${ZERO.toFixed(2)}x`);
        triggerHoldingOff();
        setFlightSpeed(0);
        setIsLuckyRive(false);
        break;
      case BalloonStatus.PREHEATING:
      case BalloonStatus.HEATING:
        triggerHoldingOn();
        if (status === BalloonStatus.HEATING) {
          setFlightSpeed(0.2);
        }
        break;
      case BalloonStatus.PAUSED:
        triggerHoldingOff();
        setFlightSpeed(0);
        break;
    }
  }, [status, vmInstance]);

  // Update multiplier display in Rive
  useEffect(() => {
    if (!vmInstance || status === BalloonStatus.IDLE) return;
    setMultiplier(`${multiplier.toFixed(2)}x`);
  }, [multiplier, vmInstance, status]);

  // Lucky granted — flip gold skin exactly once per round
  useReduxAction(balloonActions.wsBalloonLucky.type, () => {
    if (!vmInstance) return;
    setIsLuckyRive(true);
  });

  // One-shot end-game triggers — fire exactly once per action dispatch, not on re-render
  useReduxAction<PayloadAction<{ status: string }>>(balloonActions.wsUpdate.type, (action) => {
    if (!vmInstance) return;
    if (action.payload.status === BalloonStatus.WON) {
      setFlightSpeed(0);
      triggerCashout();
      triggerFinish();
    } else if (action.payload.status === BalloonStatus.BUSTED) {
      setFlightSpeed(0);
      triggerFinish();
      triggerBurst();
    }
  });

  return <RiveComponent style={{ width: '100%', height: '100%' }} />;
}
